import { Repeat } from "lucide-react"
import { parseISO, isBefore, isAfter } from "date-fns"
import { Controller, useFormContext } from "react-hook-form"
import { Switch } from "@/components/ui/switch"
import { Checkbox } from "@/components/ui/checkbox"
import { cn } from "@/lib/utils"
import useUserEventSideSheet from "@/modules/calendar/stores/use-user-event-side-sheet"
import getDaysOfWeekInfo from "@/modules/calendar/utils/get-days-of-week-info"
import useCalendarConfig from "@/modules/calendar/stores/use-calendar-config"
import isARecurringEvent from "@/modules/calendar/utils/is-a-recurring-event"

export default function EventReplay() {
  const { ranges, currentWeek } = useCalendarConfig()
  const { startDate, endDate } = ranges
  const { isDetails, event } = useUserEventSideSheet()
  const { control, watch, setValue } = useFormContext()
  const daysOfWeekInfo = getDaysOfWeekInfo()
  const canShowReccurringDays: boolean = watch("canShowReccurringDays")

  const wantToSwitchFromRecurringToSingle =
    isARecurringEvent(event) && !watch("canShowReccurringDays")

  if (isDetails) return null

  const isInvalidDay = (dayValue: number) => {
    const currentWeekStartDate = parseISO(currentWeek.startDate)
    const currentDate = new Date(currentWeekStartDate)
    currentDate.setDate(
      currentDate.getDate() + (dayValue === 0 ? 7 : dayValue) - currentDate.getDay(),
    )
    return isBefore(currentDate, parseISO(startDate)) || isAfter(currentDate, parseISO(endDate))
  }

  return (
    <div className="flex w-full flex-col gap-6">
      <div className="flex w-full items-center gap-3">
        <Repeat className="!size-5 shrink-0 text-gray-500" />
        <div className="flex w-full items-center justify-start gap-2 truncate text-gray-600">
          <span className="text-sm font-normal">Se repite:</span>
          <span className="flex items-center justify-center truncate text-sm font-semibold first-letter:capitalize">
            <Switch
              checked={canShowReccurringDays}
              onCheckedChange={(checked) => setValue("canShowReccurringDays", checked)}
            />
          </span>
        </div>
      </div>

      {wantToSwitchFromRecurringToSingle && (
        <div className="mt-[-15px] w-[250px] select-none overflow-hidden rounded-lg border border-neutral-200 bg-white p-2 text-xs shadow-lg">
          <p>Los eventos recurrentes no pueden pasar a ser eventos únicos.</p>
          <p>Intenta eliminar los recurrentes y luego crea un evento único.</p>
        </div>
      )}

      {canShowReccurringDays && (
        <div className="flex w-full items-center gap-4">
          <span className="shrink-0 text-sm font-normal text-gray-500">En</span>
          <div className="flex w-full items-center gap-3">
            <Controller
              control={control}
              name="recurringDays"
              render={({ field }) => (
                <div className="flex items-center gap-3">
                  {daysOfWeekInfo.map((dayInfo) => {
                    const isChecked = field.value?.includes(dayInfo.value)
                    const isDisabled = isInvalidDay(dayInfo.value)
                    return (
                      <div key={dayInfo.value} className="relative">
                        <div
                          className={cn(
                            "flex size-8 cursor-pointer select-none items-center justify-center rounded-full border text-xs font-semibold capitalize transition-colors",
                            isChecked
                              ? "border-primary-700 bg-primary-700 text-white"
                              : "border-gray-300 bg-gray-100 text-gray-500",
                            isDisabled && "pointer-events-none select-none opacity-50",
                          )}
                          onClick={() => {
                            if (isChecked) {
                              field.onChange(
                                field.value?.filter((value: number) => value !== dayInfo.value),
                              )
                            } else {
                              field.onChange([...(field.value || []), dayInfo.value])
                            }
                          }}
                        >
                          {dayInfo.label}
                        </div>
                        <Checkbox
                          className="sr-only"
                          checked={isChecked}
                          onCheckedChange={(checked) => {
                            return checked
                              ? field.onChange([...(field.value || []), dayInfo.value])
                              : field.onChange(
                                  field.value?.filter((value: number) => value !== dayInfo.value),
                                )
                          }}
                        />
                      </div>
                    )
                  })}
                </div>
              )}
            />
          </div>
        </div>
      )}
    </div>
  )
}
